// src/app/signin/page.tsx
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { InputText } from 'primereact/inputtext'
import { Password } from 'primereact/password'
import { useEffect, useState } from 'react'
import './signin-view.scss'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'layout/context/auth-context'
import { useToast } from 'layout/context/toast-context'

const HERO_TITLE = 'Monitore seu condomínio com tecnologia de ponta'

const SigninView = () => {
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [formError, setFormError] = useState<string>('')
  const [isShaking, setIsShaking] = useState(false)
  const [displayedTitle, setDisplayedTitle] = useState('')
  const [typingDone, setTypingDone] = useState(false)
  const [precisionValue, setPrecisionValue] = useState(0)

  const { showSuccess, showError } = useToast()
  const { login, isLoading, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated) {
      console.info('✅ SignIn: Already authenticated, redirecting...')
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    let i = 0
    const timer = setInterval(() => {
      i++
      setDisplayedTitle(HERO_TITLE.slice(0, i))
      if (i >= HERO_TITLE.length) {
        clearInterval(timer)
        setTypingDone(true)
      }
    }, 40)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const startTime = performance.now()
    const duration = 2000
    const target = 99.8
    let animId: number
    const animate = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setPrecisionValue(parseFloat((eased * target).toFixed(1)))
      if (progress < 1) animId = requestAnimationFrame(animate)
    }
    animId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animId)
  }, [])

  const triggerShake = () => {
    setIsShaking(true)
    setTimeout(() => setIsShaking(false), 600)
  }

  const validateForm = (): boolean => {
    setFormError('')

    if (!email.trim()) {
      setFormError('Email/Usuário é obrigatório')
      triggerShake()
      return false
    }

    if (!password) {
      setFormError('Senha é obrigatória')
      triggerShake()
      return false
    }

    if (password.length < 3) {
      setFormError('Senha deve ter pelo menos 3 caracteres')
      triggerShake()
      return false
    }

    return true
  }

  const handleLogin = async () => {
    setFormError('')

    if (!validateForm()) {
      return
    }

    try {
      console.info('🔐 SignIn: Submitting login...')
      await login(email, password)
      showSuccess('Login bem-sucedido', 'Redirecionando...')
    } catch (error) {
      console.error('❌ SignIn: Login failed:', error)

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Email/usuário ou senha incorretos. Tente novamente.'

      setFormError(errorMessage)
      triggerShake()
      showError('Login Falhou', errorMessage)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin()
    }
  }

  return (
    <div className='signin-page'>
      {/* Left Side - Landing Content */}
      <div className='landing-section'>
        <div className='landing-bg'>
          <div className='blob blob-1' />
          <div className='blob blob-2' />
          <div className='blob blob-3' />
          <div className='grid-overlay' />
          <div className='scan-line' />
        </div>

        <div className='landing-content'>
          {/* Brand Logo Area */}
          <div className='landing-header'>
            <div className='brand-logo-area'>
              <div className='brand-logo-image-slot'>
                <img
                  src='/logo.png'
                  alt='RT Guard'
                  className='brand-logo-img'
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <div className='brand-logo-icon-fallback'>
                  <i className='pi pi-video'></i>
                </div>
              </div>
              <div className='brand-logo-text'>
                <h1 className='logo-title-large'>RT GUARD</h1>
                <p className='logo-subtitle-large'>
                  Segurança Inteligente em Tempo Real
                </p>
              </div>
            </div>
          </div>

          {/* Hero Section */}
          <div className='landing-hero'>
            <h2 className='hero-title'>
              {displayedTitle}
              {!typingDone && <span className='cursor'>|</span>}
            </h2>
            <p className='hero-description'>
              Sistema completo de reconhecimento veicular e facial com análise
              em tempo real, alertas inteligentes e relatórios detalhados para
              máxima segurança.
            </p>
          </div>

          {/* Features Grid */}
          <div className='features-grid'>
            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='pi pi-car'></i>
              </div>
              <h3 className='feature-title'>ANPR</h3>
              <p className='feature-description'>
                Reconhecimento automático de placas veiculares
              </p>
            </div>

            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='pi pi-eye'></i>
              </div>
              <h3 className='feature-title'>Reconhecimento Facial</h3>
              <p className='feature-description'>
                Identificação e classificação de pessoas
              </p>
            </div>

            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='pi pi-bolt'></i>
              </div>
              <h3 className='feature-title'>Tempo Real</h3>
              <p className='feature-description'>
                Monitoramento e alertas instantâneos
              </p>
            </div>

            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='pi pi-chart-bar'></i>
              </div>
              <h3 className='feature-title'>Relatórios</h3>
              <p className='feature-description'>
                Análises detalhadas e exportação de dados
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className='landing-stats'>
            <div className='stat-item'>
              <div className='stat-number'>{precisionValue}%</div>
              <div className='stat-label'>Precisão</div>
            </div>
            <div className='stat-item'>
              <div className='stat-number'>24/7</div>
              <div className='stat-label'>Monitoramento</div>
            </div>
            <div className='stat-item'>
              <div className='stat-number'>&lt;1s</div>
              <div className='stat-label'>Resposta</div>
            </div>
          </div>

          {/* Footer */}
          <div className='landing-footer'>
            <p>© 2024 RT Guard. Desenvolvido por BSC Tecnologia</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className='login-section'>
        <div className='login-container'>
          {/* Logo display area above the form */}
          <div className='login-brand'>
            <div className='login-brand-logo-slot'>
              <img
                src='/logo.png'
                alt='RT Guard'
                className='login-brand-logo-img'
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
              <div className='login-brand-icon-fallback'>
                <i className='pi pi-shield'></i>
              </div>
            </div>
            <div className='login-brand-text'>
              <span className='login-brand-name'>RT GUARD</span>
              <span className='login-brand-tag'>SISTEMA DE SEGURANÇA</span>
            </div>
          </div>

          <Card
            title={
              <div className='login-header'>
                <h2 className='login-title'>Área do Cliente</h2>
                <p className='login-subtitle'>Acesse sua conta</p>
              </div>
            }
            className={`login-card${isShaking ? ' shake' : ''}`}
          >
            <div className='login-form p-fluid'>
              {formError && (
                <div className='p-message p-message-error mb-3'>
                  <span className='p-message-icon pi pi-times-circle'></span>
                  <span className='p-message-detail'>{formError}</span>
                </div>
              )}

              <div className='field'>
                <span className='p-float-label'>
                  <InputText
                    id='email'
                    value={email}
                    disabled={isLoading}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setFormError('')
                    }}
                    onKeyDown={handleKeyDown}
                    className='w-full'
                    autoComplete='username'
                    autoFocus
                  />
                  <label htmlFor='email'>Email/Usuário</label>
                </span>
              </div>

              <div className='field'>
                <span className='p-float-label'>
                  <Password
                    id='password'
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setFormError('')
                    }}
                    onKeyDown={handleKeyDown}
                    toggleMask
                    disabled={isLoading}
                    feedback={false}
                    className='w-full'
                    autoComplete='current-password'
                  />
                  <label htmlFor='password'>Senha</label>
                </span>
              </div>

              <div className='field'>
                <Button
                  onClick={handleLogin}
                  label={isLoading ? 'Entrando...' : 'Entrar'}
                  icon='pi pi-sign-in'
                  className='w-full login-button'
                  loading={isLoading}
                  disabled={isLoading}
                />
              </div>

              <div className='login-help'>
                <a href='#' className='help-link'>
                  Esqueceu sua senha?
                </a>
              </div>
            </div>
          </Card>

          <div className='login-info'>
            <p className='info-text'>
              <i className='pi pi-lock'></i>
              Conexão segura e criptografada
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SigninView
